import { formatDate } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  TemplateRef,
  ContentChild,
  forwardRef,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  NativeDateAdapter,
} from '@angular/material/core';
import { Subject } from 'rxjs';
import {
  normalizeRichTextHtml,
  sanitizeRichTextHtml,
} from 'src/app/shared/utils/rich-text-content.utils';
import { formatDateToYmd } from 'src/app/shared/utils/formatDate';

export interface InlineEditorOption {
  value: unknown;
  label: string;
  disabled?: boolean;
}

export type InlineEditorFieldType = 
  'text'
  | 'number'
  | 'date'
  | 'textarea'
  | 'richtext'
  | 'select'
  | 'checkbox'
  | 'custom';

export const INLINE_EDITOR_DATE_FORMATS = {
  parse: {
    dateInput: 'input',
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export class InlineEditorDateAdapter extends NativeDateAdapter {
  constructor(dateLocale: string) {
    super(dateLocale);
  }

  parse(value: unknown): Date | null {
    if (typeof value !== 'string') {
      return value instanceof Date ? this.cloneDate(value) : super.parse(value);
    }

    const rawValue = value.trim();
    if (!rawValue) {
      return null;
    }

    const ymdMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymdMatch) {
      return this.createValidatedDate(ymdMatch[1], ymdMatch[2], ymdMatch[3]);
    }

    const isoMatch = rawValue.match(/^(\d{4})-(\d{2})-(\d{2})T/);
    if (isoMatch) {
      return this.createValidatedDate(isoMatch[1], isoMatch[2], isoMatch[3]);
    }

    const mdyDashMatch = rawValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (mdyDashMatch) {
      return this.createValidatedDate(mdyDashMatch[3], mdyDashMatch[1], mdyDashMatch[2]);
    }

    const mdySlashMatch = rawValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (mdySlashMatch) {
      return this.createValidatedDate(mdySlashMatch[3], mdySlashMatch[1], mdySlashMatch[2]);
    }

    const parsed = super.parse(value);
    return parsed instanceof Date && !isNaN(parsed.getTime()) ? this.cloneDate(parsed) : parsed;
  }

  deserialize(value: unknown): Date | null {
    const parsed = this.parse(value);
    if (parsed instanceof Date && !isNaN(parsed.getTime())) {
      return parsed;
    }

    const deserialized = super.deserialize(value);
    return deserialized instanceof Date && !isNaN(deserialized.getTime())
      ? this.cloneDate(deserialized)
      : deserialized;
  }

  format(date: Date, displayFormat: unknown): string {
    if (displayFormat === 'input') {
      return this.toMdy(date);
    }

    return super.format(date, displayFormat as object);
  }

  private cloneDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private createValidatedDate(year: string, month: string, day: string): Date | null {
    const parsedYear = Number(year);
    const parsedMonth = Number(month);
    const parsedDay = Number(day);
    const date = new Date(parsedYear, parsedMonth - 1, parsedDay);

    if (
      Number.isNaN(date.getTime()) ||
      date.getFullYear() !== parsedYear ||
      date.getMonth() !== parsedMonth - 1 ||
      date.getDate() !== parsedDay
    ) {
      return null;
    }

    return date;
  }

  private toMdy(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }
}

@Component({
  selector: 'app-inline-editor',
  templateUrl: './inline-editor.component.html',
  styleUrls: ['./inline-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InlineEditorComponent),
      multi: true
    },
    {
      provide: DateAdapter,
      useFactory: (dateLocale: string) => new InlineEditorDateAdapter(dateLocale),
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: INLINE_EDITOR_DATE_FORMATS,
    },
  ]
})
export class InlineEditorComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input() fieldType: InlineEditorFieldType = 'text';
  @Input() placeholder: string = 'Click to edit';
  @Input() emptyText: string = 'Click to edit';
  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input()
  set value(val: unknown) {
    if (this.shouldSkipExternalWrite()) {
      return;
    }

    this.internalValue = val;
    // If we're currently editing, we need to update the form control value as well
    if (this.isEditing && this.fieldType === 'select' && this.selectElement?.nativeElement) {
      this.selectElement.nativeElement.value = val !== null && val !== undefined ? String(val) : '';
    }
    // Para campos de fecha, sincronizar internalValueDate con el valor formateado
    if(this.fieldType === 'date') {
      if (this.internalValue) {
        this.internalValueDate = this.formattedDateValue;
      } else {
        this.internalValueDate = '';
      }
    }
  }
  get value(): unknown {
    return this.internalValue;
  }
  @Input() rows: number = 4; // For textarea
  @Input() min: number | undefined;
  @Input() max: number | undefined;
  @Input() step: number | undefined;
  @Input() options: InlineEditorOption[] = []; // For select
  @Input() displayFormatter: (value: unknown) => string = (value) => value?.toString() || '';
  @Input() valueParser: (value: string) => unknown = (value) => value;
  @Input() cssClass: string = '';
  @Input() editCssClass: string = '';
  @Input() autoFocus: boolean = true;
  @Input() autoSelect: boolean = false; // For select dropdowns
  @Input() confirmOnEnter: boolean = true;
  @Input() cancelOnEscape: boolean = true;
  @Input() saveOnBlur: boolean = true;
  @Input() showEditIcon: boolean = true;
  @Input() editIconClass: string = 'fas fa-edit';
  @Input() loadingIconClass: string = 'fas fa-spinner fa-spin';
  @Input() isLoading: boolean = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Output() valueChange = new EventEmitter<any>();
  @Output() editStart = new EventEmitter<void>();
  @Output() editEnd = new EventEmitter<void>();
  @Output() editCancel = new EventEmitter<void>();

  @ViewChild('inputElement', { static: false }) inputElement!: ElementRef<HTMLInputElement>;
  @ViewChild('textareaElement', { static: false }) textareaElement!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('selectElement', { static: false }) selectElement!: ElementRef<HTMLSelectElement>;

  @ContentChild('customTemplate', { static: false }) customTemplate!: TemplateRef<unknown>;
  @ContentChild('displayTemplate', { static: false }) displayTemplate!: TemplateRef<unknown>;

  @ViewChild('picker') datepicker!: MatDatepicker<Date>;

  private destroy$ = new Subject<void>();

  isEditing = false;
  internalValue: unknown = null;
  originalValue: unknown = null;
  internalValueDate: string = '';
  internalValueDateObject: Date | null = null; // Para Material Datepicker
  private justStartedEditing = false;
  wasCalendarOpen = false;
  dateValidated: boolean = true;

  private static activeEditor: InlineEditorComponent | null = null;
  private static isListenerAttached = false;

  // ControlValueAccessor implementation
  private onChange: (value: unknown) => void = () => {};
  private onTouched = () => {};

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  ngAfterViewInit() {
    // Auto-focus when entering edit mode, but not for select elements
    if (this.isEditing && this.autoFocus && this.fieldType !== 'select') {
      this.focusInput();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor methods
  writeValue(value: unknown): void {
    if (this.shouldSkipExternalWrite()) {
      return;
    }

    this.internalValue = value;

    // If we're currently editing a select, update the DOM element as well
    if (this.isEditing && this.fieldType === 'select' && this.selectElement?.nativeElement) {
      this.selectElement.nativeElement.value = value !== null && value !== undefined ? String(value) : '';
    }

    // Para campos de fecha, sincronizar internalValueDate cuando el valor cambia desde afuera
    if (this.fieldType === 'date') {
      if (this.internalValue) {
        this.internalValueDate = this.formattedDateValue;
      } else {
        this.internalValueDate = '';
      }
    }
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  startEditing(): void {
    if (this.disabled || this.readonly || this.isLoading || this.isEditing) {
      return;
    }

    InlineEditorComponent.attachGlobalListener();
    InlineEditorComponent.activeEditor = this;

    this.originalValue = this.internalValue;
    this.isEditing = true;
    this.justStartedEditing = true;
    this.editStart.emit();

    // Para campos de fecha, inicializar internalValueDate con el valor actual formateado
    if (this.fieldType === 'date' && this.internalValue) {
      this.internalValueDate = this.formattedDateValue;
      // Inicializar el objeto Date para Material Datepicker
      if (this.internalValue instanceof Date) {
        this.internalValueDateObject = this.internalValue;
      } else if (typeof this.internalValue === 'string') {
        // Parsear la fecha en zona horaria local para evitar desplazamientos
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(this.internalValue);
        if (match) {
          const [, year, month, day] = match;
          this.internalValueDateObject = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else {
          this.internalValueDateObject = new Date(this.internalValue);
        }
      } else {
        this.internalValueDateObject = null;
      }
    } else if (this.fieldType === 'date' && !this.internalValue) {
      // Si no hay valor, limpiar internalValueDate
      this.internalValueDate = '';
      this.internalValueDateObject = null;
    }

    // Reset the flag after a brief delay to allow the current click event to complete
    setTimeout(() => {
      this.justStartedEditing = false;
    }, 50);

    // Auto-focus for non-select elements after a brief delay to ensure DOM is ready
    if (this.fieldType !== 'select') {
      setTimeout(() => {
        if (this.isEditing) { // Double-check we're still editing
          this.focusInput();
        }
      }, 10);
    }
  }

  stopEditing(save: boolean = true): void {
    if (!this.isEditing) {
      return;
    }

    if (save) {
      this.saveValue();
    } else {
      this.cancelEdit();
    }

    this.isEditing = false;
    this.justStartedEditing = false; // Reset flag when stopping
    this.editEnd.emit();

    if (InlineEditorComponent.activeEditor === this) {
      InlineEditorComponent.activeEditor = null;
    }
  }

  cancelEdit(): void {
    this.internalValue = this.originalValue;
    this.editCancel.emit();
  }

  saveValue(): void {
    const valueToParse = this.fieldType === 'richtext'
      ? normalizeRichTextHtml(this.internalValue as string)
      : this.internalValue;
    const parsedValue = this.parseValue(valueToParse as string | number | Date | null | undefined);
    this.onChange(parsedValue);
    this.onTouched();
    this.valueChange.emit(parsedValue);
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    this.internalValue = target.value;

    // For select elements, save and exit immediately
    if (this.fieldType === 'select') {
      this.saveValue();
      this.isEditing = false;
      this.editEnd.emit();
    }
  }

  onCustomValueChange(value: unknown): void {
    this.internalValue = value;
  }

  onRichTextChange(value: string | null): void {
    this.internalValue = sanitizeRichTextHtml(value || '');
  }

  onSaveCustom(newValue?: unknown): void {
    const isEventValue = this.isEventLike(newValue) && newValue.target !== undefined;

    if (newValue !== undefined && newValue !== null && !isEventValue) {
      this.internalValue = newValue;
    }
    this.stopEditing(true);
  }

  onCancelCustom(): void {
    this.stopEditing(false);
  }

  onInputDateChange(event: string): void {
    console.log('onInputDateChange event:', event);
    this.internalValueDate = event;
    // Si el campo está vacío, establecer null; de lo contrario, usar el valor del campo
    this.internalValue = this.internalValueDate ? this.internalValueDate : null;

    if(!this.internalValueDate) {
      this.dateValidated = false;
    }
    else {
      this.dateValidated = true;
    }
  }

  onInputDateObjectChange(event: Date | null): void {
    console.log('onInputDateObjectChange event:', event);
    this.internalValueDateObject = event;

    // Allow empty
    if (!event) {
      this.internalValue = null;
      this.dateValidated = true;
      return;
    }

    // Convertir el Date object a string en formato Y-m-d en zona horaria local
    if (event && event instanceof Date && !isNaN(event.getTime())) {
      const year = event.getFullYear();
      const month = String(event.getMonth() + 1).padStart(2, '0');
      const day = String(event.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      this.internalValue = dateString;
      this.dateValidated = true;
    } else {
      this.internalValue = null;
      this.dateValidated = false;
    }
  }

  onInputDate(event:Event){
    const value = (event.target as HTMLInputElement).value;

    // quitar el error si el campo queda vacio
    if(value === '') {
      this.dateValidated = true;
      this.internalValueDate = '';
      this.internalValue = null; // Establecer null en lugar de cadena vacía
      return;
    }
  }

  onOpened() {
    // calendar opened
    this.wasCalendarOpen = true;
  }

  onClosed() {
    // calendar closed => user either selected a date or closed the picker
    this.wasCalendarOpen = false;
  }

  onDateChange(event: MatDatepickerInputEvent<Date>) {
    void event;
    if (this.wasCalendarOpen) {
      // User selected a date from the calendar
      this.saveValue();
      this.isEditing = false;
      this.editEnd.emit();
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.confirmOnEnter) {
      if ((this.fieldType !== 'textarea' && this.fieldType !== 'richtext') || event.ctrlKey) {
        event.preventDefault();
        this.stopEditing(true);
      }
    } else if (event.key === 'Escape' && this.cancelOnEscape) {
      event.preventDefault();
      this.stopEditing(false);
    }
  }

  onBlur(): void {
    if (this.saveOnBlur && this.fieldType !== 'select') {
      this.stopEditing(true);
    }
  }

  onSelectBlur(): void {
    // For select elements, save and exit if still editing
    if (this.isEditing) {
      // Small delay to allow change event to fire first
      setTimeout(() => {
        if (this.isEditing) {
          this.stopEditing(true);
        }
      }, 10);
    }
  }

  private focusInput(): void {
    if (!this.autoFocus || this.fieldType === 'select') {
      return; // Never auto-focus select elements
    }

    let element: HTMLElement | null = null;

    switch (this.fieldType) {
      case 'text':
      case 'number':
      case 'date':
        element = this.inputElement?.nativeElement;
        break;
      case 'textarea':
        element = this.textareaElement?.nativeElement;
        break;
    }

    if (element) {
      element.focus();

      // Select all text for text inputs only
      if ((this.fieldType === 'text' || this.fieldType === 'number') && this.autoSelect) {
        (element as HTMLInputElement).select();
      }
    }
  }

  private parseValue(value: string | number | Date | null | undefined): unknown {
    // Normalizar valores vacíos a null para todos los tipos de campo
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    if (this.fieldType === 'number') {
      const num = typeof value === 'string' ? parseFloat(value) : (typeof value === 'number' ? value : NaN);
      return isNaN(num) ? null : num;
    }

    // For date fields, convert string input to Date object
    if (this.fieldType === 'date' && typeof value === 'string') {
      // If it's an ISO string, create Date object
      if (value.includes('T')) {
        return new Date(value);
      }
    }

    return this.valueParser(value as string);
  }

  private isEventLike(value: unknown): value is { target?: unknown } {
    return typeof value === 'object' && value !== null && 'target' in value;
  }

  private shouldSkipExternalWrite(): boolean {
    // Keep current in-editor rich text state stable while the user is typing.
    return this.isEditing && this.fieldType === 'richtext';
  }

  get displayValue(): string {
    if (this.internalValue === null || this.internalValue === undefined || this.internalValue === '') {
      return this.emptyText;
    }

    if (this.fieldType === 'select') {
      const option = this.options.find(opt => opt.value === this.internalValue);
      return option ? option.label : String(this.internalValue);
    }

    if (this.fieldType === 'date' && this.internalValue) {
      // If it's a Date object, use it directly
      if (this.internalValue instanceof Date) {
        return formatDate(this.internalValue, 'MM-dd-yyyy', 'en-US').toString();
      }

      // Handle Y-m-d format strings properly to avoid timezone issues
      if (typeof this.internalValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(this.internalValue)) {
        const [year, month, day] = this.internalValue.split('-').map(Number);
        const date = new Date(year, month - 1, day); // Create date in local timezone
        return formatDate(date, 'MM-dd-yyyy', 'en-US').toString();
      }

      // Handle ISO strings by extracting date part first
      if (typeof this.internalValue === 'string' && this.internalValue.includes('T')) {
        const dateStr = formatDateToYmd(this.internalValue) ?? '';
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return formatDate(date, 'MM-dd-yyyy', 'en-US').toString();
      }

      // Fallback for other formats
      if (typeof this.internalValue === 'string' || typeof this.internalValue === 'number') {
        const date = new Date(this.internalValue);
        if (!isNaN(date.getTime())) {
          return formatDate(date, 'MM-dd-yyyy', 'en-US').toString();
        }
      }
    }

    if (this.fieldType === 'richtext') {
      return normalizeRichTextHtml(this.internalValue as string);
    }

    return this.displayFormatter(this.internalValue);
  }

  get isEmpty(): boolean {
    return this.internalValue === null || 
           this.internalValue === undefined ||
           this.internalValue === '';
  }

  get inputType(): string {
    switch (this.fieldType) {
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      default:
        return 'text';
    }
  }

  get richTextMinHeight(): number {
    return Math.max(80, this.rows * 24);
  }

  get richTextValue(): string {
    return normalizeRichTextHtml(this.internalValue as string);
  }

  get formattedDateValue(): string {
    if (this.fieldType === 'date' && this.internalValue) {
      return formatDateToYmd(this.internalValue as string | Date) ?? '';
    }
    return typeof this.internalValue === 'string' ? this.internalValue : '';
  }

  setToday() {
    const today = new Date();
    this.internalValueDateObject = today;

    this.internalValue = formatDateToYmd(today) ?? '';

    this.onChange(this.internalValue);
    this.datepicker.select(today);
    this.saveValue();
    this.isEditing = false;
    this.editEnd.emit();
  }

  private static attachGlobalListener() {
    if (InlineEditorComponent.isListenerAttached) return;

    document.addEventListener('mousedown', (event: MouseEvent) => {
      const editor = InlineEditorComponent.activeEditor;
      if (!editor) return;

      const target = event.target as Node;

      if (editor.hostElement.nativeElement.contains(target)) return;

      if (!editor.saveOnBlur || editor.fieldType !== 'richtext') return;

      editor.stopEditing(true);
    });

    InlineEditorComponent.isListenerAttached = true;
  }
}
